/**
 * MySchedulePage.tsx
 * Personal weekly schedule for the current user — restyled on the Employee
 * Schedule UX-overhaul tech (single inline timeline, lane-stacked bars, real
 * Graph avatars), while keeping its existing behaviour:
 *   - scoped client-side to the authenticated user (useAuth().user.appUserKey);
 *   - read-only (task assignments managed by coordinators);
 *   - "+ Add Absence" → AddAssignmentDialog with restrictToAbsence, locked to me;
 *   - capacity legend, week nav, 45s live-refresh, last-synced footer.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Alert, Button, IconButton, useTheme } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import TodayIcon from '@mui/icons-material/Today';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../../auth/useAuth';
import { useEmployeeScheduleData } from '../../../hooks/useEmployeeScheduleData';
import { useGraphPhotos } from '../../../hooks/useGraphPhotos';
import { useScheduleAutoRefresh } from '../../../hooks/useScheduleAutoRefresh';
import { useScheduleHolidays } from '../../../hooks/useScheduleHolidays';
import { EmployeeMatrixView } from './EmployeeMatrixView';
import { AssignmentDetailDrawer } from '../../../components/schedule/AssignmentDetailDrawer';
import { AddAssignmentDialog } from './AddAssignmentDialog';
import { currentSaturday, addWeeksISO, fridayAfter } from '../../../components/schedule/employeeScheduleShared';
import { capacityHeatColor, capacityHeatGradientCss } from '../../../utils/scheduleCapacity';
import type { EmployeeBar } from '../../../services/scheduleEmployeeBars';

const WEEK_COUNT = 8;
const noop = () => undefined;

function fmt(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export const MySchedulePage: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const currentUserKey = user?.appUserKey ?? null;
  const currentUserName = user?.displayName ?? 'My Schedule';

  const [anchorWeek, setAnchorWeek] = useState<string>(() => currentSaturday());
  const windowStart = anchorWeek;
  const windowEnd = fridayAfter(anchorWeek, WEEK_COUNT);
  const data = useEmployeeScheduleData(windowStart, windowEnd);
  // P-22 (audit 2026-07-01): same holiday-aware weekly % as the Employee
  // Schedule matrix — without this, My Schedule would disagree with it in
  // stat-holiday weeks (review-mandated fix). Best-effort: failure -> /5.
  const { nameByDate: holidayNames } = useScheduleHolidays(windowStart, windowEnd);
  const holidaySet = useMemo(() => new Set(holidayNames.keys()), [holidayNames]);

  // Scope everything to me + merge my Graph headshot.
  const myEmails = useMemo(
    () => data.users.filter((u) => u.userKey === currentUserKey).map((u) => u.email).filter(Boolean),
    [data.users, currentUserKey],
  );
  const photos = useGraphPhotos(myEmails);
  const myUsers = useMemo(
    () => data.users.filter((u) => u.userKey === currentUserKey).map((u) => ({ ...u, photoUrl: photos.get(u.email) ?? u.photoUrl })),
    [data.users, currentUserKey, photos],
  );
  const myBars = useMemo(() => data.bars.filter((b) => b.userKey === currentUserKey), [data.bars, currentUserKey]);

  const [expandedUserKey, setExpandedUserKey] = useState<string | null>(null);
  const [drawerBar, setDrawerBar] = useState<EmployeeBar | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  // Auto-expand my row once data arrives.
  const didExpand = useRef(false);
  useEffect(() => {
    if (!didExpand.current && currentUserKey && myUsers.length > 0) {
      didExpand.current = true;
      setExpandedUserKey(currentUserKey);
    }
  }, [currentUserKey, myUsers]);

  const { lastSyncedLabel } = useScheduleAutoRefresh({ reload: data.reload, paused: !!drawerBar });

  const drawerUser = useMemo(() => myUsers.find((u) => u.userKey === drawerBar?.userKey) ?? null, [myUsers, drawerBar]);

  // ── profile-loading guard ──────────────────────────────────────────────────
  if (!currentUserKey) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 2, minHeight: 300 }}>
        <PersonIcon sx={{ fontSize: 56, color: theme.schedule.employeeAlloc, opacity: 0.35 }} />
        <Typography variant="body2" color="text.secondary">Your user profile is loading. Please wait…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <Box sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>My Schedule</Typography>
        <Typography variant="body2" color="text.secondary">Your personal weekly assignment view.</Typography>
      </Box>

      {/* toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Button size="small" variant="outlined" startIcon={<TodayIcon />} onClick={() => setAnchorWeek(currentSaturday())}>Today</Button>
        <IconButton size="small" onClick={() => setAnchorWeek((w) => addWeeksISO(w, -1))}><ChevronLeftIcon /></IconButton>
        <IconButton size="small" onClick={() => setAnchorWeek((w) => addWeeksISO(w, 1))}><ChevronRightIcon /></IconButton>
        <Typography variant="body2" fontWeight={600} sx={{ mx: 0.5 }}>{fmt(windowStart)} – {fmt(windowEnd)}</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, ml: 'auto' }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary">Capacity Guide:</Typography>
          {/* Weekly cell = % of the week's working days busy (Q5B). Lighter =
              fewer busy days, darker = every working day busy. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              aria-hidden="true"
              sx={{ width: 56, height: 10, borderRadius: '3px', backgroundImage: capacityHeatGradientCss(), border: `1px solid ${theme.border.color}` }}
            />
            <Typography variant="caption" color="text.secondary">Free → every day busy</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              aria-hidden="true"
              sx={{ width: 10, height: 10, borderRadius: '3px', backgroundColor: capacityHeatColor(160).bg, boxShadow: `inset 0 0 0 2px ${theme.schedule.heatmapHigh}` }}
            />
            <Typography variant="caption" color="text.secondary">Conflict — assigned on a day off</Typography>
          </Box>
        </Box>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add Absence</Button>
      </Box>

      {data.error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => data.setError(null)}>{data.error}</Alert>}

      {/* read-only banner */}
      <Alert severity="info" icon={false} sx={{ mt: 1, py: 0.5, fontSize: '0.78rem', backgroundColor: theme.schedule.selectionBg }}>
        <strong>Read-only view.</strong> Task assignments are managed by schedule coordinators. Use "+ Add Absence" to log your own Vacation, Sick, Field Work, or Other absence entries.
      </Alert>

      {/* body */}
      <Box sx={{ flex: 1, overflow: 'hidden', mt: 0.5 }}>
        {data.loading ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Loading your schedule…</Typography>
        ) : myUsers.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <PersonIcon sx={{ fontSize: 48, color: theme.schedule.employeeAlloc, opacity: 0.3, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">Your user account is not in the schedule system yet.</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>Contact a schedule coordinator to be added as an assignable user.</Typography>
          </Box>
        ) : (
          <EmployeeMatrixView
            users={myUsers} bars={myBars} capacityByKey={data.capacityByKey} weekStarts={data.weekStarts}
            expandedUserKey={expandedUserKey} onToggleExpand={(k) => setExpandedUserKey((p) => (p === k ? null : k))}
            onBarClick={(bar) => setDrawerBar(bar)} holidays={holidaySet}
          />
        )}
      </Box>

      {/* footer */}
      <Box sx={{ pt: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
          Showing your assignments only · last synced {lastSyncedLabel}
        </Typography>
      </Box>

      {/* read-only detail drawer */}
      <AssignmentDetailDrawer
        open={!!drawerBar} bar={drawerBar} user={drawerUser} readOnly
        onClose={() => setDrawerBar(null)}
        onReassign={noop} onDelete={noop}
      />

      {/* Add Absence — absence types only, locked to me */}
      <AddAssignmentDialog
        open={addOpen} onClose={() => setAddOpen(false)}
        onSuccess={() => { setAddOpen(false); void data.reload({ quiet: true }); }}
        prefillUserKey={currentUserKey} prefillUserName={currentUserName} prefillWeekStart={anchorWeek}
        restrictToAbsence
      />
    </Box>
  );
};
